import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { Toaster } from 'app/shared/lib/toaster';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/api/api';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { openStagedRecordsSummary } from 'app/core/components/summary/job/staged-records-summary';
import { deepClone } from 'app/shared/lib/utilities';
import { ImportsService, IImportHistorySearchFilters, IImportHistoryOrderBy } from 'app/core/services/imports-service';
import { ImportStatusAndHistoryReloadEvent } from 'app/um/lib/events';
import { EventAggregator } from 'aurelia-event-aggregator';
import * as Core from 'app/core/models/essette-api';

@autoinject()
export class JobDetailInformation extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected importJobId: string | null = null;

    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected importJobSystemCode: string | null = null;

    protected importJob: Core.ImportJob | null = null;
    protected staticImportJob: Core.ImportJob | null = null;
    protected importJobTenantKey: string | null = null;

    protected allowDelete: boolean = false;
    protected showStagedRecordsLink: boolean = false;

    protected openStagedRecordsSummary = openStagedRecordsSummary;
    private sorting = new Sorting();

    ////****************** Element References ***************************************////



    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: ImportsService,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        ...rest: any[]) {

        super(...rest);
    }
    protected bind(): void {
        this.checkPermissions();
        this.reload();
    }


    ////****************** Events ***************************************************////
    public async reload(): Promise<void> {
        await this.loadImportJob();
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    ////****************** Functions ************************************************////

    protected buildQueryParams(): Partial<IImportHistorySearchFilters> {
        let queryParams: Partial<IImportHistorySearchFilters> = {};

        queryParams.systemCode = this.importJobSystemCode;
        queryParams.stagedJobIdentifier = this.importJobId;

        let orderByTables: IImportHistoryOrderBy[] = [];

        this.sorting.entries.forEach(item => {
            orderByTables.push({
                property: item.reference,
                descending: item.direction === "descending"
            })
        });

        if(orderByTables.length > 0) {
            queryParams.orderBy = {
                "tables": orderByTables || null
            };
        }

        return queryParams;
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.allowDelete = this.accountContext.hasPermission('Core.ImportHistory.Remove.DeleteStaged');
        this.showStagedRecordsLink = this.accountContext.hasRouteAccess('core.imports.job.staged');
    }

    protected buildImportJobTable(importTenant: Core.ImportTenant, importTable: Core.ImportTable): Core.ImportHistory {
        let importHistory: Core.ImportHistory = {
            systemCode: this.importJobSystemCode,
            stagedJobIdentifier: this.importJobId,
            tenant: {
                key: importTenant.key
            },
            table: {
                name: importTable.section?.table?.name
            }
        };

        if(importHistory.table && importTable.section?.table?.filter?.field !== null && importTable.section?.table?.filter?.value !== null) {
            importHistory.table.filter = {
                field: importTable.section?.table?.filter?.field,
                value: importTable.section?.table?.filter?.value
            }
        }

        return importHistory;
    }

    protected openStagedRecords(section: Core.PolicySection, importTenant: Core.ImportTenant) {
        const importSection: Core.PolicySection = {
            $display: section.$display,
            category: section.category,
            systemCode: this.importJobSystemCode,
            stagedJobIdentifier: this.importJobId,
            tenant: {
                key: importTenant.key
            },
            table: section.table
        };

        openStagedRecordsSummary(importSection);
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadImportJob(): Promise<void> {
        let queryParams = this.buildQueryParams()

        try {
            this.state.beginLoad();

            const response = await this.service.loadImportHistory(queryParams);
            this.importJob = response.result.jobs[0];

            // Eric: staticImportJob is used to avoid the rerendering from `repeat.for` of the tenant cards to retain table sort.
            if(this.importJob &&
                (this.staticImportJob?.stagedJobIdentifier !== this.importJob.stagedJobIdentifier ||
                    this.staticImportJob?.stagedSystemCode !== this.importJob.stagedSystemCode)) {
                this.staticImportJob = deepClone(this.importJob);
            }

        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    public async removeImportHistory(importTable: Core.ImportTable, importTenant: Core.ImportTenant) {
        if(!this.allowDelete && !importTable.section) return;

        const result = await confirmation.showDelete('core:importHistory.job.tenant.table.section.$', 1, false);
        if(result.dialogResponse === confirmation.options.CANCEL) { return; }

        if(!(result.dialogResponse === confirmation.options.OK)) {
            return;
        }

        let isSuccessful: boolean = false;
        try {
            this.state.beginSave();
            const response = await this.service.removeImportHistory(this.buildImportJobTable(importTenant, importTable));

            isSuccessful = response.isSuccessful;

            if(isSuccessful) {
                this.reload();
                this.events.publish(new ImportStatusAndHistoryReloadEvent());
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.deleted('core:importHistory.job.tenant.table.section.$', isSuccessful);
            this.state.endSave();
        }
    }
}
